import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Steps, Step } from "@/components/ui/steps";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Info, Code, Check, Upload, FileCode, AlertTriangle, Terminal } from "lucide-react";

const OTABuildGuide: React.FC = () => {
  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Build a Firmware File for OTA Update</CardTitle>
        <CardDescription>
          Turn a code change into a <code>.bin</code> file that can be sent to your devices over the air
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Alert className="mb-4 bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-500" />
          <AlertTitle>What you need</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>Visual Studio Code with the PlatformIO extension installed</li>
              <li>This project folder opened in VS Code (synced via GitHub Desktop or cloned locally)</li>
              <li>A USB cable is only needed for the very first flash; after that, updates can be sent over WiFi</li>
            </ul>
          </AlertDescription>
        </Alert>

        <Steps>
          <Step icon={<Code className="h-5 w-5" />} title="Open the Project in VS Code">
            <p className="text-sm text-muted-foreground mt-2">
              Open the project folder in Visual Studio Code. You should see the PlatformIO alien icon in the left sidebar,
              and the file <code>platformio.ini</code> in the root of the explorer.
            </p>
          </Step>

          <Step icon={<FileCode className="h-5 w-5" />} title="Edit the Code">
            <p className="text-sm text-muted-foreground mt-2">
              Make your changes in the <code>src/</code> folder. For example, open <code>src/SlaveESP32.cpp</code> or
              <code> src/AccentMeteorPattern.h</code> and adjust the values you want to change.
            </p>
            <Alert className="mt-2 bg-yellow-50 border-yellow-200">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertTitle>Tip</AlertTitle>
              <AlertDescription>
                Keep changes small and test one thing at a time. That makes it easier to spot which change caused a problem.
              </AlertDescription>
            </Alert>
          </Step>

          <Step icon={<Terminal className="h-5 w-5" />} title="Pick the Correct Environment">
            <p className="text-sm text-muted-foreground mt-2">
              In the PlatformIO sidebar, open <strong>Project Tasks</strong>. You will see two environments:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-muted-foreground">
              <li><code>env:master</code> — for the controller device with the PIR sensor</li>
              <li><code>env:slave</code> — for the LED sculpture devices</li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              Expand the environment that matches the device you want to update. There is no default environment on purpose,
              so you cannot accidentally build the wrong firmware.
            </p>
          </Step>

          <Step icon={<Info className="h-5 w-5" />} title="Check the Firmware Version">
            <p className="text-sm text-muted-foreground mt-2">
              The version number is generated automatically. Whenever any firmware source file changes, the build bumps the
              patch number and uses the same version for master and slave. The current value is stored in:
            </p>
            <code className="block mt-2 p-2 bg-muted rounded text-xs">firmware_version.json</code>
            <p className="text-sm text-muted-foreground mt-2">
              The build log prints the version it used, for example <code>[auto_version] source changed -&gt; firmware version v3.0.4</code>.
              Enter that exact version in the dashboard upload form. To force a major or minor bump, edit the
              <code> version</code> field in <code>firmware_version.json</code> before building.
            </p>
          </Step>

          <Step icon={<Check className="h-5 w-5" />} title="Build the Firmware">
            <p className="text-sm text-muted-foreground mt-2">
              Click <strong>Build</strong> under the chosen environment. PlatformIO will compile the project and show the result
              in the terminal panel at the bottom.
            </p>
            <Alert className="mt-2 bg-green-50 border-green-200">
              <Check className="h-4 w-4 text-green-500" />
              <AlertTitle>Successful build</AlertTitle>
              <AlertDescription>
                Look for the line that says <code>[SUCCESS]</code> and note the path to the generated <code>.bin</code> file.
                It will look similar to:
                <code className="block mt-1 p-2 bg-muted rounded text-xs">.pio/build/slave/firmware.bin</code>
                or
                <code className="block mt-1 p-2 bg-muted rounded text-xs">.pio/build/master/firmware.bin</code>
              </AlertDescription>
            </Alert>
          </Step>

          <Step icon={<Upload className="h-5 w-5" />} title="Upload the .bin to the Dashboard">
            <p className="text-sm text-muted-foreground mt-2">
              Open the Perpetual Plexus dashboard and go to the <strong>Firmware</strong> page. Click <strong>Upload Firmware</strong>,
              choose the <code>.bin</code> file you just built, and set:
            </p>
            <ul className="list-disc pl-5 mt-2 space-y-1 text-sm text-muted-foreground">
              <li><strong>Type:</strong> master or slave</li>
              <li><strong>Version:</strong> a version number such as <code>v2.7.0</code></li>
              <li><strong>Series:</strong> the device series this firmware is meant for, for example <code>S1</code> or <code>ALL</code></li>
            </ul>
            <p className="text-sm text-muted-foreground mt-2">
              The firmware is uploaded as a draft first. Use the <strong>Release</strong> button to make it available to devices.
            </p>
          </Step>
        </Steps>

        <Alert className="mt-6 bg-orange-50 border-orange-200">
          <AlertTriangle className="h-4 w-4 text-orange-500" />
          <AlertTitle>First flash still needs USB</AlertTitle>
          <AlertDescription>
            OTA updates only work once a device already has firmware that knows how to talk to your WiFi and the dashboard.
            A brand-new ESP32 must first be flashed over USB using the <strong>Upload</strong> task in PlatformIO.
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default OTABuildGuide;
